import React from 'react'
import { HiExclamationTriangle, HiCheckCircle, HiInformationCircle } from 'react-icons/hi2'

interface ConfidenceIndicatorProps {
  confidence: number
  reliability?: string
  sampleSize: number
  dataSources?: {
    reddit_count: number
    twitter_count: number
    total_count: number
  }
  className?: string
}

const ConfidenceIndicator: React.FC<ConfidenceIndicatorProps> = ({
  confidence,
  reliability,
  sampleSize,
  dataSources,
  className = ''
}) => {
  const formattedConfidence =
    Number.isFinite(confidence) && confidence >= 0
      ? Math.round(confidence * 100)
      : null
  const getConfidenceColor = (reliability?: string) => {
    if (!reliability) return 'text-gray-300 bg-gray-900/40 border-gray-700'
    switch (reliability.toLowerCase()) {
      case 'very_high':
        return 'text-blue-300 bg-blue-900/30 border-blue-700'
      case 'high':
        return 'text-green-300 bg-green-900/30 border-green-700'
      case 'medium':
        return 'text-yellow-300 bg-yellow-900/30 border-yellow-700'
      case 'low':
        return 'text-orange-300 bg-orange-900/30 border-orange-700'
      case 'very_low':
        return 'text-red-300 bg-red-900/30 border-red-700'
      default:
        return 'text-gray-300 bg-gray-900/40 border-gray-700'
    }
  }

  const getConfidenceIcon = (reliability?: string) => {
    if (!reliability) return <HiInformationCircle className="w-4 h-4" />
    switch (reliability.toLowerCase()) {
      case 'very_high':
      case 'high':
        return <HiCheckCircle className="w-4 h-4" />
      case 'medium':
        return <HiInformationCircle className="w-4 h-4" />
      case 'low':
      case 'very_low':
        return <HiExclamationTriangle className="w-4 h-4" />
      default:
        return <HiInformationCircle className="w-4 h-4" />
    }
  }

  const getConfidenceLabel = (reliability?: string) => {
    if (!reliability) return 'Unknown Confidence'
    switch (reliability.toLowerCase()) {
      case 'very_high':
        return 'Very High Confidence'
      case 'high':
        return 'High Confidence'
      case 'medium':
        return 'Medium Confidence'
      case 'low':
        return 'Low Confidence'
      case 'very_low':
        return 'Very Low Confidence'
      default:
        return 'Unknown Confidence'
    }
  }

  const getConfidenceDescription = (sampleSize: number, reliability?: string) => {
    if (!reliability) return `Based on ${sampleSize} fan comments`
    switch (reliability.toLowerCase()) {
      case 'very_high':
        return `Based on ${sampleSize} fan comments - extremely reliable`
      case 'high':
        return `Based on ${sampleSize} fan comments - very reliable`
      case 'medium':
        return `Based on ${sampleSize} fan comments - moderately reliable`
      case 'low':
        return `Based on ${sampleSize} fan comments - limited data`
      case 'very_low':
        return `Based on ${sampleSize} fan comments - saying one or both teams have really low data`
      default:
        return `Based on ${sampleSize} fan comments`
    }
  }

  return (
    <div className={`w-full rounded-lg border p-4 sm:p-5 ${getConfidenceColor(reliability)} ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        {getConfidenceIcon(reliability)}
        <span className="font-medium text-xs sm:text-sm">
          {getConfidenceLabel(reliability)}
        </span>
        
      </div>
      
      <p className="text-[11px] sm:text-xs opacity-90 mb-2">
        {getConfidenceDescription(sampleSize, reliability)}
      </p>
      {formattedConfidence !== null && (
        <p className="text-[11px] sm:text-xs opacity-75 mb-2">
          Confidence Score: {formattedConfidence}%
        </p>
      )}
      
      {/* Data source breakdown */}
      {dataSources && (
        <div className="text-[11px] sm:text-xs opacity-75">
          <div className="flex justify-between">
            <span>Reddit: {dataSources.reddit_count}</span>
            <span>Total: {dataSources.total_count}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default ConfidenceIndicator
